/**
 * Utility functions for form input manipulation
 */

import type { MedicationSuggestionGroup } from "./prescriptionUtils";
import type { PharmacySuggestionGroup } from "./prescriptionUtils";

/** Trailing pharmacy clause from chip merge: ``and use`` / ``and select`` / ``and send``. */
const TRAILING_AND_PHARMACY_RE = /\s+and (?:use|select|send)\s+(.+)$/i;

/** User-visible assignment line (parsed by orchestration as med -> pharmacy, not final send). */
export const formatMedicationPharmacyAssignment = (
  medicationName: string,
  pharmacyName: string
): string => {
  const med = medicationName.trim();
  const pharmacy = pharmacyName.trim();
  if (!med) {
    return pharmacy ? `Select ${pharmacy}` : "";
  }
  if (!pharmacy) {
    return "";
  }
  const pharmacyLabel = /\bpharmacy$/i.test(pharmacy) ? pharmacy : `${pharmacy} pharmacy`;
  return `Select ${pharmacyLabel} for ${med} medication`;
};

/** Read trimmed prescription text from a Formik innerRef. */
export const getPrescriptionText = (formikRef: React.MutableRefObject<any>): string =>
  String(formikRef?.current?.values?.prescriptionText ?? "").trim();

/**
 * A medication/pharmacy strength picker can show the identical option text
 * (e.g. "Lisinopril 30 MG Oral Tablet") across two unrelated rounds for the
 * same drug name — the catalog's available strengths don't change between
 * rounds, only the request that triggered the picker does. Keying picks by
 * medication name alone means a pick made in an earlier round keeps matching
 * a later round's freshly-rendered chip by text coincidence, highlighting it
 * as if the user had already chosen it this round. Scoping the storage key
 * by the conversation turn the picker belongs to keeps each round's picks
 * independent, without changing anything else that reads the map back out.
 */
export const conversationScopedPickKey = (
  conversationId: number | string | undefined,
  medicationName: string
): string =>
  conversationId !== undefined && conversationId !== null
    ? `${conversationId}::${medicationName}`
    : medicationName;

/** Project a conversation-scoped picks map back down to plain medication-name keys for one round. */
export const picksForConversation = (
  picksByCompositeKey: Record<string, string>,
  conversationId: number | string | undefined
): Record<string, string> => {
  const prefix = `${conversationId}::`;
  const result: Record<string, string> = {};
  Object.entries(picksByCompositeKey || {}).forEach(([key, value]) => {
    if (key.startsWith(prefix)) {
      result[key.slice(prefix.length)] = value;
    }
  });
  return result;
};

function stripTrailingPharmacyClause(text: string): { base: string; pharmacyRest: string | null } {
  const m = text.match(TRAILING_AND_PHARMACY_RE);
  if (!m || m.index === undefined) {
    return { base: text.trim(), pharmacyRest: null };
  }
  return {
    base: text.slice(0, m.index).trim(),
    pharmacyRest: m[1].trim(),
  };
}

/**
 * After a pharmacy chip click: keep existing medication/free text and append
 * `` and select <pharmacy>``. ``Select``-only lines become ``Select <pharmacy>``.
 */
export const mergePharmacyChipIntoInput = (
  current: string,
  pharmacyName: string,
  medicationName?: string
): string => {
  const trimmed = (current || "").trim();
  const { base } = stripTrailingPharmacyClause(trimmed);
  const pharmacyClause = medicationName
    ? formatMedicationPharmacyAssignment(medicationName, pharmacyName)
    : `select ${pharmacyName}`;
  if (!base) {
    return medicationName ? pharmacyClause : `Select ${pharmacyName}`.trim();
  }
  if (/^select\s+/i.test(base)) {
    return medicationName ? pharmacyClause : `Select ${pharmacyName}`.trim();
  }
  return `${base} and ${pharmacyClause}`.replace(/\s+/g, " ").trim();
};

/**
 * Build send text from one pick per medication group (compound multi-Rx).
 * Order follows ``suggestionGroups`` when provided, else insertion order.
 */
export const buildCombinedMedicationPickMessage = (
  picksByMedicationName: Record<string, string>,
  suggestionGroups?: MedicationSuggestionGroup[]
): string => {
  // No pending medication picker this round (e.g. a pharmacy-only round after the
  // medication was already resolved earlier) — nothing to restate. Do NOT fall back to
  // dumping every historical pick in `picksByMedicationName`: those maps persist across
  // turns (so chips stay highlighted after send) and would otherwise smuggle a stale,
  // already-resolved medication pick into an unrelated later message.
  if (!suggestionGroups?.length) {
    return "";
  }
  const lines: string[] = [];
  for (const g of suggestionGroups) {
    const pick = picksByMedicationName[g.medication_name];
    if (pick?.trim()) {
      lines.push(pick.trim());
    }
  }
  return lines.join("\n");
};

export const buildCombinedPharmacyPickClauses = (
  picksByMedicationName: Record<string, string>,
  pharmacyGroups?: PharmacySuggestionGroup[]
): string => {
  // Same reasoning as buildCombinedMedicationPickMessage: only restate picks that are
  // part of THIS round's pending pharmacy groups, never the full historical map.
  if (!pharmacyGroups?.length) {
    return "";
  }
  const lines: string[] = [];
  for (const g of pharmacyGroups) {
    const pick = picksByMedicationName[g.medication_name];
    if (pick?.trim()) {
      lines.push(
        formatMedicationPharmacyAssignment(g.medication_name, pick.trim())
      );
    }
  }
  return lines.join(" and ");
};

export const buildCombinedMedicationAndPharmacyPickMessage = (
  medicationPicksByName: Record<string, string>,
  pharmacyPicksByName: Record<string, string>,
  suggestionGroups?: MedicationSuggestionGroup[],
  pharmacyGroups?: PharmacySuggestionGroup[]
): string => {
  const medicationText = buildCombinedMedicationPickMessage(
    medicationPicksByName,
    suggestionGroups
  );
  const pharmacyText = buildCombinedPharmacyPickClauses(
    pharmacyPicksByName,
    pharmacyGroups
  );
  return [medicationText, pharmacyText].filter(Boolean).join(" and ");
};

export const mergeMedicationChipIntoInput = (current: string, medicationLine: string): string => {
  const trimmed = (current || "").trim();
  const { pharmacyRest } = stripTrailingPharmacyClause(trimmed);
  if (pharmacyRest) {
    return `${medicationLine} and select ${pharmacyRest}`.replace(/\s+/g, " ").trim();
  }
  const useOnly = /^use\s+(.+)$/i.exec(trimmed);
  if (useOnly) {
    return `${medicationLine} and select ${useOnly[1].trim()}`.replace(/\s+/g, " ").trim();
  }
  const selectOnly = /^select\s+(.+)$/i.exec(trimmed);
  if (selectOnly) {
    return `${medicationLine} and select ${selectOnly[1].trim()}`.replace(/\s+/g, " ").trim();
  }
  return medicationLine;
};

/**
 * Focuses the prescription text input and sets cursor to end
 */
export const focusInputWithCursorAtEnd = (
  text: string,
  formikRef: React.MutableRefObject<any>
): void => {
  if (!text || !formikRef?.current) {
    return;
  }

  // Set the value in the form using formikRef
  formikRef.current.setFieldValue('prescriptionText', text);
  
  // Focus the input field and set cursor to end
  setTimeout(() => {
    // Memory leak prevention: check if formikRef still exists and input element exists
    if (formikRef?.current) {
      const inputElement = document.getElementById('prescriptionText') as HTMLInputElement | HTMLTextAreaElement;
      if (inputElement) {
        inputElement.focus();
        // Set cursor to end of text
        if (inputElement.setSelectionRange) {
          const length = text.length;
          inputElement.setSelectionRange(length, length);
        }
      }
    }
  }, 0);
};

